import type { FC } from 'react';
import React from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { Modal } from '@motorway/motorway-storybook-cra';
import { Button } from '@motorway/mw-highway-code';
import { InfoBox } from '@motorway/mw-highway-code/alpha';

import cypressIds from 'Components/cypress_ids.json';
import { triggerBulkBuyingPromotionModalGotItClicked } from 'Services/analytics/events/bulkBuyingPromotionEvents';
import { gbpCurrencyFormatter } from 'Utilities/formatters';
import { applyTestIdData } from 'Utilities/helpers';

import { calculateDiscount } from '../calculateDiscount';
import texts from '../PromotionsBanner.texts';
import type { DiscountValues } from '../useDiscountValues';

import styles from './PromotionsBannerModal.module.scss';

const LocalT = new MDText(texts);

const ModalContent: FC<{ discountValues: DiscountValues; dismissModal?: () => void }> = ({
	discountValues,
	dismissModal,
}) => {
	const { percentageDiscount, purchaseThreshold } = discountValues;
	const { feesOnThresholdVehicles, totalSavings } = calculateDiscount({ percentageDiscount, purchaseThreshold });

	const formattedFeesOnThresholdVehicles = gbpCurrencyFormatter().format(feesOnThresholdVehicles);
	const formattedTotalSavings = gbpCurrencyFormatter().format(totalSavings);

	return (
		<div className={styles.modalContentContainer}>
			<div className={styles.headerContainer}>
				<h3 className={styles.title}>{LocalT.translate(texts.modal.header)}</h3>
			</div>
			<InfoBox
				content={LocalT.translate(texts.modal.infoBox.description, { percentageDiscount, purchaseThreshold })}
				heading={LocalT.translate(texts.modal.infoBox.title, { percentageDiscount })}
				variant="success"
			/>
			<div className={styles.howItWorksContainer}>
				<h4 className={styles.howItWorksTitle}>{LocalT.translate(texts.modal.howItWorks.title)}</h4>
				<ol>
					<li>
						<div className={styles.howItWorksNumberContainer}>
							<span {...applyTestIdData(cypressIds.text.promotionsStepOneNumber)}>
								{LocalT.translate(texts.modal.howItWorks.one)}
							</span>
						</div>
						<span {...applyTestIdData(cypressIds.text.promotionsStepOneDescription)}>
							{LocalT.translate(texts.modal.howItWorks.step1, { purchaseThreshold })}
						</span>
					</li>
					<li>
						<div className={styles.howItWorksNumberContainer}>
							<span {...applyTestIdData(cypressIds.text.promotionsStepTwoNumber)}>
								{LocalT.translate(texts.modal.howItWorks.two)}
							</span>
						</div>
						<span {...applyTestIdData(cypressIds.text.promotionsStepTwoDescription)}>
							{LocalT.translate(texts.modal.howItWorks.step2, { percentageDiscount })}
						</span>
					</li>
				</ol>
			</div>

			<table className={styles.exampleTable}>
				<thead>
					<tr>
						<th colSpan={2} scope="column">
							{LocalT.translate(texts.modal.example.example)}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr className={styles.exampleRow}>
						<th scope="row">{LocalT.translate(texts.modal.example.vehiclePurchases, { purchaseThreshold })}</th>
						<td>
							{LocalT.translate(texts.modal.example.vehiclePurchasesTotal, {
								feesOnThresholdVehicles: formattedFeesOnThresholdVehicles,
							})}
						</td>
					</tr>
					<tr className={cx(styles.exampleRow, styles.green)}>
						<th scope="row">{LocalT.translate(texts.modal.example.discountApplied, { percentageDiscount })}</th>
						<td>
							{LocalT.translate(texts.modal.example.discountAppliedTotal, {
								totalSavings: formattedTotalSavings,
							})}
						</td>
					</tr>
				</tbody>
				<tfoot>
					<tr className={cx(styles.exampleRow)}>
						<th scope="row">{LocalT.translate(texts.modal.example.totalSaving, { percentageDiscount })}</th>
						<td>
							{LocalT.translate(texts.modal.example.totalSavingAmount, {
								totalSavings: formattedTotalSavings,
							})}
						</td>
					</tr>
				</tfoot>
			</table>

			<div className={styles.footer}>
				<Button
					fullWidth
					reverse
					label={LocalT.translate(texts.modal.buttonText)}
					onClick={() => {
						triggerBulkBuyingPromotionModalGotItClicked();
						dismissModal?.();
					}}
				/>
			</div>
		</div>
	);
};

interface PromotionsBannerModalProps {
	discountValues: DiscountValues;
	onDismissModal?: () => void;
}

export const PromotionsBannerModal: FC<PromotionsBannerModalProps> = ({ discountValues, onDismissModal }) => {
	return (
		<Modal
			contentClass={styles.modalContent}
			dialogClass={styles.modalDialog}
			dismissModal={onDismissModal}
			size="small"
		>
			<ModalContent discountValues={discountValues} dismissModal={onDismissModal} />
		</Modal>
	);
};
